import { useRef, type ReactNode, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { u2000ShouldInvokeDialogBackdropClick } from "../../utils/u2000ShouldInvokeDialogBackdropClick";

type WidthPreset = "small" | "medium" | "large" | "x-large";
type WidthProp = WidthPreset | string;

interface U2000BasicDialogProps {
  isOpen: boolean;
  onClose: (reason: string) => void;
  title: ReactNode;
  children: ReactNode;
  width?: WidthProp;
  height?: string;
  style?: CSSProperties;
  /** Si true, le contenu ne tronque pas les éléments en overflow (ex. liste d'un combobox). */
  contentOverflowVisible?: boolean;
  /** Classes Tailwind pour le conteneur plein écran (ex. `z-100` pour empiler au-dessus d'une autre modale). */
  overlayClassName?: string;
  /** Clic sur la zone assombrie hors panneau (target = conteneur overlay). */
  onBackdropClick?: () => void;
}

const presetMap: Record<WidthPreset, string> = {
  small: "max-w-sm",
  medium: "w-[700px]",
  large: "w-[900px]",
  "x-large": "w-[1100px]",
};

function resolveWidth(w?: WidthProp): string {
  if (!w) return presetMap["medium"];

  if (w === "small" || w === "medium" || w === "large" || w === "x-large") {
    return presetMap[w as WidthPreset];
  }

  if (typeof w === "string" && /^(w-|max-w-)/.test(w)) {
    return w;
  }

  return presetMap["medium"];
}

function U2000BasicDialog({
  isOpen,
  onClose,
  title,
  children,
  width = "medium",
  height,
  style,
  contentOverflowVisible = false,
  overlayClassName,
  onBackdropClick,
}: U2000BasicDialogProps) {
  /** True seulement si le mousedown a démarré sur l’overlay (évite fermeture après sélection texte). */
  const backdropPointerDownRef = useRef(false);

  if (!isOpen) return null;

  const resolvedWidth = resolveWidth(width);
  const heightClass = height || "max-h-[90vh]";
  const fixedHeight = typeof heightClass === "string" && /^h-/.test(heightClass);
  const usesMaxHeight = typeof heightClass === "string" && /^max-h-/.test(heightClass);
  const mainClass = contentOverflowVisible
    ? "flex min-h-0 flex-1 flex-col overflow-visible"
    : fixedHeight
      ? "min-h-0 flex-1 overflow-y-auto"
      : usesMaxHeight
        ? "flex min-h-0 flex-1 flex-col overflow-hidden"
        : "min-h-0 overflow-y-auto";
  const containerOverflowClass = contentOverflowVisible ? "overflow-visible" : "overflow-hidden";
  /* Ne pas imposer max-w-[95vw] si l’appelant fournit déjà un max-w-* (ex. éditeur PDF quasi plein écran). */
  const maxWidthClass = /\bmax-w-/.test(resolvedWidth) ? "" : "max-w-[95vw]";

  const overlayClasses = overlayClassName ?? "z-50";
  /* Si l’appelant impose un padding (ex. `p-2` éditeur PDF), ne pas forcer `p-4`. */
  const overlayPaddingClass = /\bp-/.test(overlayClasses) ? "" : "p-4";

  return createPortal(
    <div
      className={`fixed inset-0 ${overlayClasses} flex items-center justify-center ${overlayPaddingClass} backdrop-blur-xs bg-black/40 animate-fade-in`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      onMouseDown={
        onBackdropClick
          ? (e) => {
              backdropPointerDownRef.current = e.target === e.currentTarget;
            }
          : undefined
      }
      onClick={
        onBackdropClick
          ? (e) => {
              const startedOnBackdrop = backdropPointerDownRef.current;
              backdropPointerDownRef.current = false;
              if (
                u2000ShouldInvokeDialogBackdropClick(
                  startedOnBackdrop,
                  e.target === e.currentTarget
                )
              ) {
                onBackdropClick();
              }
            }
          : undefined
      }
    >
      <div
        className={`${resolvedWidth} ${maxWidthClass} ${heightClass} ${containerOverflowClass} bg-surface rounded-2xl shadow-2xl border border-gray-200 animate-scale-in flex flex-col`}
        style={{ ...style }}
        role="document"
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-muted rounded-t-2xl shrink-0">
          <h2 id="dialog-title" className="text-lg font-semibold text-gray-900">
            {title}
          </h2>
          <button
            className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200 focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            onClick={() => onClose("close")}
            aria-label="Fermer la dialogue"
            title="Fermer"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </header>

        <main className={mainClass}>{children}</main>
      </div>
    </div>,
    document.body,
  );
}

export default U2000BasicDialog;
